import { CarData } from "@/src/pages/consumer/HomePage/homePageUtils";

import { UnifiedCarouselImage } from "@/src/types/carousel";
import { AuctionItemMediaDTO, MediaService } from "@/src/types/types";
import {
  AspectRatio,
  ImageSizeWithOriginal,
  getImageUrl
} from "@/src/utils/imageUtils";

/**
 * Resolves lightbox URL with fallback logic.
 * Tries lightboxSize if provided, otherwise tries "xl" then "lg".
 *
 * @param key - Media key
 * @param aspectRatio - Aspect ratio for the image
 * @param mediaService - Media service to use
 * @param imageUrl - Fallback image URL
 * @param lightboxSize - Optional specific size to try first
 * @returns Resolved lightbox URL or fallback imageUrl
 */
const resolveLightboxUrl = (
  key: string,
  aspectRatio: AspectRatio,
  mediaService: MediaService,
  imageUrl: string,
  lightboxSize?: ImageSizeWithOriginal
): string => {
  if (lightboxSize) {
    try {
      return getImageUrl(key, lightboxSize, aspectRatio, mediaService);
    } catch (error: unknown) {
      const errMsg = error instanceof Error ? error.message : String(error);
      console.warn(
        `Failed to generate lightbox URL with size ${lightboxSize}`,
        errMsg
      );
      return imageUrl;
    }
  }

  try {
    return getImageUrl(key, "xl", aspectRatio, mediaService);
  } catch (xlError: unknown) {
    try {
      return getImageUrl(key, "lg", aspectRatio, mediaService);
    } catch (lgError: unknown) {
      const xlErrMsg =
        xlError instanceof Error ? xlError.message : String(xlError);
      const lgErrMsg =
        lgError instanceof Error ? lgError.message : String(lgError);
      console.warn(`Failed to generate lightbox URL, falling back`, {
        xlError: xlErrMsg,
        lgError: lgErrMsg
      });
      return imageUrl;
    }
  }
};

/**
 * Maps AuctionItemMediaDTO to UnifiedCarouselImage.
 * Uses getImageUrl to generate URLs based on the media service and key.
 *
 * @param media - The auction item media DTO
 * @param size - Image size to use for the main URL (default: "md")
 * @param aspectRatio - Aspect ratio for the image (default: "4/3")
 * @returns UnifiedCarouselImage instance
 */
export const mapAuctionItemMediaToCarouselImage = (
  media: AuctionItemMediaDTO,
  size: ImageSizeWithOriginal = "md",
  aspectRatio: AspectRatio = "4/3",
  lightboxSize?: ImageSizeWithOriginal
): UnifiedCarouselImage => {
  const mediaService = media.mediaService ?? MediaService.AISAV2;
  const imageUrl = getImageUrl(media.key, size, aspectRatio, mediaService);

  // For thumbnails, use valid combinations:
  // - sm + 16/9 (valid for AISA_V2)
  // - md + 4/3 (fallback if aspectRatio is 4/3)
  const thumbnailSize = aspectRatio === "16/9" ? "sm" : "md";
  const thumbnailAspectRatio = aspectRatio === "16/9" ? "16/9" : "4/3";
  const thumbnailUrl = getImageUrl(
    media.key,
    thumbnailSize,
    thumbnailAspectRatio,
    mediaService
  );

  const lightboxUrl = resolveLightboxUrl(
    media.key,
    aspectRatio,
    mediaService,
    imageUrl,
    lightboxSize
  );

  // Generate original URL for lightbox progressive loading
  let originalUrl: string | undefined;
  try {
    originalUrl = getImageUrl(media.key, "original", aspectRatio, mediaService);
  } catch (error: unknown) {
    // If original size is not available, don't set originalUrl
    const errMsg = error instanceof Error ? error.message : String(error);
    console.warn(
      `Failed to generate original URL for media key ${media.key}`,
      errMsg
    );
    originalUrl = undefined;
  }

  return {
    id: media.key,
    url: imageUrl,
    alt: media.from,
    thumbnailUrl,
    lightboxUrl,
    originalUrl
  };
};

/**
 * Maps CarData (homepage carousel data) to UnifiedCarouselImage.
 * CarData contains url, model, and price fields.
 *
 * @param car - The car data from homepage
 * @returns UnifiedCarouselImage instance
 */
export const mapCarDataToCarouselImage = (
  car: CarData
): UnifiedCarouselImage => {
  return {
    id: car.url,
    url: car.url,
    alt: car.model,
    // Additional fields like price can be stored in meta for future use
    meta: {
      model: car.model,
      price: car.price
    }
  };
};

/**
 * Maps AAMUI ImageCarouselItem to UnifiedCarouselImage.
 * ImageCarouselItem from AAMUI has an image property with src and alt.
 *
 * @param item - The AAMUI ImageCarouselItem
 * @returns UnifiedCarouselImage instance
 */
export const mapAamuiImageItemToCarouselImage = (item: {
  image: {
    src: string;
    alt?: string;
  };
  hasAdditionalInfo?: boolean;
  additionalInfoCreatedAt?: string;
}): UnifiedCarouselImage => {
  return {
    id: item.image.src,
    url: item.image.src,
    alt: item.image.alt,
    hasAdditionalInfo: item.hasAdditionalInfo,
    createdAt: item.additionalInfoCreatedAt
  };
};

/**
 * Maps AuctionAdditionalInfoDTO media to UnifiedCarouselImage.
 *
 * @param media - Media item from AuctionAdditionalInfoDTO
 * @param size - Image size to use for the main URL (default: "lg")
 * @param aspectRatio - Aspect ratio for the image (default: "4/3")
 * @param createdAt - Optional creation date for the additional info entry
 * @returns UnifiedCarouselImage instance
 */
export const mapAdditionalInfoMediaToCarouselImage = (
  media: {
    id: number;
    key: string;
    from: string;
    mediaService: MediaService;
  },
  size: ImageSizeWithOriginal = "lg",
  aspectRatio: AspectRatio = "4/3",
  createdAt?: string
): UnifiedCarouselImage => {
  const mediaService = media.mediaService ?? MediaService.AISAV2;
  const imageUrl = getImageUrl(media.key, size, aspectRatio, mediaService);

  const lightboxUrl = resolveLightboxUrl(
    media.key,
    aspectRatio,
    mediaService,
    imageUrl
  );

  // Generate original URL for lightbox progressive loading
  let originalUrl: string | undefined;
  try {
    originalUrl = getImageUrl(media.key, "original", aspectRatio, mediaService);
  } catch (error: unknown) {
    // If original size is not available, don't set originalUrl
    const errMsg = error instanceof Error ? error.message : String(error);
    console.warn(
      `Failed to generate original URL for media key ${media.key}`,
      errMsg
    );
    originalUrl = undefined;
  }

  return {
    id: String(media.id),
    url: imageUrl,
    alt: media.from,
    lightboxUrl,
    originalUrl,
    createdAt
  };
};

/**
 * Maps File objects (selected but not uploaded) to UnifiedCarouselImage.
 * Uses object URLs for display.
 *
 * @param file - File object
 * @param objectUrl - Object URL created from the file
 * @returns UnifiedCarouselImage instance
 */
export const mapFileToCarouselImage = (
  file: File,
  objectUrl: string
): UnifiedCarouselImage => {
  return {
    id: `${file.name}-${file.lastModified}`,
    url: objectUrl,
    alt: file.name,
    lightboxUrl: objectUrl // Use same URL for lightbox since it's already high-res
  };
};
